import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, firstValueFrom, take, filter } from 'rxjs';
import { MediaDevicesList } from '@gatho/types/mediaDevices';
import { SnackBarService } from "@gatho/services/snackbar/snackbar.service";

type CustomDisplayMediaStreamOptions = {
  video?: boolean | MediaTrackConstraints & {
    displaySurface?: string;
  };
  audio?: boolean | MediaTrackConstraints & {
    suppressLocalAudioPlayback?: boolean;
  };
} & {
  preferCurrentTab?: boolean;
  selfBrowserSurface?: string;
  systemAudio?: string;
  surfaceSwitching?: string;
  monitorTypeSurfaces?: string;
};

@Injectable({
  providedIn: 'root'
})
export class MediaDevicesService {
  // Current state of device permissions
  public cameraPermission = false;
  private cameraPermissionSub = new BehaviorSubject(this.cameraPermission);
  public cameraPermission$ = this.cameraPermissionSub.asObservable();

  public microphonePermission = false;
  private microphonePermissionSub = new BehaviorSubject(this.microphonePermission);
  public microphonePermission$ = this.microphonePermissionSub.asObservable();

  private permsChecked = new BehaviorSubject(false);
  public permsChecked$ = this.permsChecked.asObservable();

  public permissionPromptRequired = false;

  public permsErrorMsg = 'An error occured accessing your media devices, please try again. Your devices may be locked for use by another application.';
  public errorMessages = {
    Incapable: 'Your web browser is not capable of using your video / audio devices',
    NotAllowedError: 'You must accept camera / microphone permissions in your web browser',
    NotFoundError: 'Unable to find your camera and/or microphone. Please ensure the devices are available and try again',
    NotReadableError: this.permsErrorMsg,
    AbortError: this.permsErrorMsg,
    Unknown: 'An unknown error occurred when accessing your camera and/or microphone. Try another browser, or restart your device and try again'
  } as const;

  // All the available input / output devices
  private devicesChecked = false;
  public availableDevices: MediaDevicesList = {videoinput: [], audioinput: [], audiooutput: []};
  private availableDevicesSub = new Subject<MediaDevicesList>();
  public availableDevices$ = this.availableDevicesSub.asObservable();

  // Chosen local media devices / streams
  public videoDeviceId?: string = localStorage.getItem('videoDeviceId') ?? undefined;
  private videoDeviceIdSub = new BehaviorSubject<string | undefined>(this.videoDeviceId);
  public videoDeviceId$ = this.videoDeviceIdSub.asObservable();

  public audioDeviceId?: string = localStorage.getItem('audioDeviceId') ?? undefined;
  private audioDeviceIdSub = new BehaviorSubject<string | undefined>(this.audioDeviceId);
  public audioDeviceId$ = this.audioDeviceIdSub.asObservable();

  public audioOutputDeviceId?: string = localStorage.getItem('audioOutputDeviceId') ?? undefined;
  private audioOutputDeviceIdSub = new BehaviorSubject<string | undefined>(this.audioOutputDeviceId);
  public audioOutputDeviceId$ = this.audioOutputDeviceIdSub.asObservable();

  // Store a flag to indicate when the user is currently configuring media settings
  public userInMediaConfig = false;
  public userInMediaConfigSub = new BehaviorSubject(this.userInMediaConfig);
  public userInMediaConfig$ = this.userInMediaConfigSub.asObservable();

  // Screen share / display capture related settings
  // @TODO: tweak defaults for red5pro performance,
  public displayMediaOptions: CustomDisplayMediaStreamOptions = {
    video: {
      displaySurface: "browser",
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 24 },
      aspectRatio: { ideal: 1.77 }  // 16:9 aspect ratio
    },
    audio: {
      suppressLocalAudioPlayback: true,
    },
    preferCurrentTab: false,
    selfBrowserSurface: "exclude",
    systemAudio: "include",
    surfaceSwitching: "include",
    monitorTypeSurfaces: "include",
  };

  constructor(
    private snackBarService: SnackBarService
  ) {
    // Check for media permissions (but don't prompt if unknown)
    this.checkMediaPermissions();
    // Listen for device changes on the system
    navigator.mediaDevices.addEventListener('devicechange', this.determineDevices.bind(this, false));
  }

  /**
   * Checks for media permissions from the browser, and determines if a prompt for permissions is required
   */
  async checkMediaPermissions() {
    // If we know the permissions are already granted, return
    if (this.cameraPermission && this.microphonePermission) {
      return;
    }
    // Track if the user needs to be prompted for perms
    let prompt = false;
    // Check for browser support of permissions API
    // most should support but things like webviews or in-app browsers may not
    if (navigator.permissions) {
      const permissionNames = ["camera", "microphone"];
      for (const perm of permissionNames) {
        try {
          const result = await navigator.permissions.query({name: perm as PermissionName});
          const permName = perm as 'camera' | 'microphone';
          let status: boolean | null = null;
          switch (result.state) {
            case 'granted':
              status = true;
            break;
            case 'denied':
              status = false;
            break;
            case 'prompt':
              prompt = true;
            break;
          }
          if (status !== null) {
            this[`${permName}Permission`] = status;
            this[`${permName}PermissionSub`].next(status);
          }
          // In the future we can probably listen to changes via permissionstatus 'change' event
          // but at the moment the browser just requires you to reload the page anyway
        }
        // Permission not supported?
        catch(e) {
          prompt = true;
          console.error(`${perm} permission not supported in this browser`);
        }
      };
      console.log('perms result from check', this.cameraPermission, this.microphonePermission);
    }
    // There isn't any other way to query perms other than to prompt
    else {
      prompt = true;
    }
    // If both permissions were explicitly denied, show a message
    if (!this.cameraPermission && !this.microphonePermission && !prompt) {
      this.showErrorMsg(this.errorMessages.NotAllowedError);
    }
    this.permissionPromptRequired = prompt;
    this.permsChecked.next(true);
  }

  /**
   * Returns a value once media permissions have been checked (but not prompted)
   */
  async permissionsChecked() {
    return await firstValueFrom(
      this.permsChecked$.pipe(
        filter(value => value),
        take(1)
      )
    );
  }

  /**
   * Check for media permissions, and prompt if not yet asked
   */
  async checkAndPromptPerms() {
    await this.permissionsChecked();
    if (this.permissionPromptRequired) {
      await this.promptMediaPermissions();
    }
  }

  /**
   * Prompts the user for device permissions
   */
  async promptMediaPermissions() {
    // no need to prompt if already granted
    if (this.cameraPermission && this.microphonePermission) {
      return;
    }
    // The permissions API is still WIP, and there is no way to ask for 2 permissions at once
    // so it's simpler to simply getUserMedia and then stop the tracks to get the initial permissions
    try {
      const stream = await navigator.mediaDevices.getUserMedia({video: true, audio: true});
      stream.getTracks().forEach((track) => track.stop());
      this.cameraPermission = true;
      this.microphonePermission = true;
    }
    // Catch if the user denied, or a diff error
    catch (e) {
      // @TODO: check for and re-request if only one type is available
      this.showError(e);
      // In any case, the permissions are not granted
      this.cameraPermission = false;
      this.microphonePermission = false;
    }
    this.cameraPermissionSub.next(this.cameraPermission);
    this.microphonePermissionSub.next(this.microphonePermission);
  }

  /**
   * Returns a value once the device list is available
   */
  async devicesAvailable() {
    if (!this.devicesChecked) {
      this.determineDevices();
      await firstValueFrom(this.availableDevices$);
    }
  }

  /**
   * Determine input and output devices
   */
  async determineDevices(forceRecheck = false) {
    if (this.devicesChecked && !forceRecheck) {
      return;
    }
    // If no support for enumerateDevices we can't do much
    if (!navigator.mediaDevices?.enumerateDevices) {
      this.showErrorMsg(this.errorMessages.Incapable);
      console.error('navigator.mediaDevices.enumerateDevices is not supported');
      return;
    }
    // We need media permissions before we can get the device info
    // @TODO: support either/or devices
    await this.checkAndPromptPerms();
    // Retrieve, filter and save the current set of devices
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const kinds: Array<keyof MediaDevicesList> = ['videoinput', 'audioinput', 'audiooutput'];
      let hasChanges = false;
      kinds.forEach((kind) => {
        const devices = allDevices.filter((device) => device.kind === kind);
        if (this.availableDevices[kind] !== devices) {
          // @ts-ignore battling 2 possible types here
          this.availableDevices[kind] = devices;
          hasChanges = true;
        }
      });
      // Set defaults if they haven't been determined yet (i.e: first time use)
      if (this.videoDeviceId === undefined && this.availableDevices.videoinput.length) {
        this.setVideoDevice(this.availableDevices.videoinput[0].deviceId);
      }
      if (this.audioDeviceId === undefined && this.availableDevices.audioinput.length) {
        this.setAudioDevice(this.availableDevices.audioinput[0].deviceId);
      }
      if (this.audioOutputDeviceId === undefined && this.availableDevices.audiooutput.length) {
        this.setAudioOutputDevice(this.availableDevices.audiooutput[0].deviceId);
      }

      if (hasChanges) {
        this.availableDevicesSub.next(this.availableDevices);
      }
    }
    catch (e) {
      this.showError(e);
    }
    this.devicesChecked = true;
  }

  setVideoDevice(deviceId: string) {
    if (deviceId !== this.videoDeviceId) {
      localStorage.setItem('videoDeviceId', deviceId);
      this.videoDeviceId = deviceId;
      this.videoDeviceIdSub.next(deviceId);
    }
  }

  setAudioDevice(deviceId: string) {
    if (deviceId !== this.audioDeviceId) {
      localStorage.setItem('audioDeviceId', deviceId);
      this.audioDeviceId = deviceId;
      this.audioDeviceIdSub.next(deviceId);
    }
  }

  setAudioOutputDevice(deviceId: string) {
    if (deviceId !== this.audioOutputDeviceId) {
      localStorage.setItem('audioOutputDeviceId', deviceId);
      this.audioOutputDeviceId = deviceId;
      this.audioOutputDeviceIdSub.next(deviceId);
    }
  }

  screenShareSupported(): boolean {
    return !!navigator.mediaDevices?.getDisplayMedia;
  }

  showError(e: unknown) {
    if (e instanceof DOMException) {
      // Show error if it's a known type
      if (e.name in this.errorMessages) {
        this.showErrorMsg(this.errorMessages[e.name as keyof typeof this.errorMessages]);
      }
    }
    else {
      // Something else has gone wrong
      this.showErrorMsg(this.errorMessages.Unknown);
    }
  }

  showErrorMsg(message: string) {
    const config = { panelClass: 'error' };
    this.snackBarService.open(message, config);
  }
}
